import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import useMasterMaps, { selCls, inputCls } from "../hooks/useMasterMaps";
import { toast } from "sonner";
import { Plus, Timer, Send, Inbox } from "lucide-react";
import { StatusBadge, TypeBadge, SlaChip, DaysInStage, MRF_STATUSES, MRF_TYPES, MRF_PRIORITIES } from "../components/MrfBits";

function AgeBadge({ days }) {
  const color = days >= 7 ? "#BB162B" : days >= 3 ? "#D97706" : "#1B7F3B";
  return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold tabular-nums" style={{ color }}>
      <Timer size={13} /> {days}d
    </span>
  );
}

const TABS = [["all", "All requisitions"], ["mine", "My requisitions"], ["tray", "Auto-drafted tray"]];

export default function Requisitions() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const maps = useMasterMaps();
  const [tab, setTab] = useState("all");
  const [tray, setTray] = useState([]);
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ status: "", requisition_type: "", priority: "", sla_status: "", branch_id: "", search: "" });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [limit, setLimit] = useState(50);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
    const main = tab === "mine" ? api.get("/mrf/mine") : api.get("/requisitions", { params });
    Promise.all([api.get("/requisitions/tray"), main])
      .then(([t, r]) => { setTray(t.data); setRows(r.data); setLimit(50); setLoadError(false); })
      .catch((e) => { toast.error(apiError(e)); setLoadError(true); })
      .finally(() => setLoading(false));
  }, [f, tab]);
  useEffect(load, [load]);

  const submit = async (r) => {
    setBusy(r.req_id);
    try {
      await api.post(`/mrf/${r.req_id}/submit`);
      toast.success(`${r.req_no} submitted for approval`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };

  const list = tab === "tray" ? [] : rows;

  return (
    <div data-testid="requisitions-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-serif text-2xl sm:text-3xl text-[#0E2240] tracking-tight">Requisitions</h1>
          <p className="text-sm text-[#6B7280] mt-1">Every open position, who is sitting on it, and for how long.</p>
        </div>
        <div className="flex gap-2 self-start">
          {can("requisitions", "approve") && (
            <button data-testid="goto-inbox-button" onClick={() => navigate("/requisitions/inbox")}
              className="flex items-center gap-2 border border-[#0E2240] text-[#0E2240] text-sm px-4 py-2 rounded-sm hover:bg-[#0E2240]/5 transition-colors duration-200">
              <Inbox size={15} /> Approval Inbox
            </button>
          )}
          {can("requisitions", "write") && (
            <button data-testid="requisition-new-button" onClick={() => navigate("/requisitions/new")}
              className="flex items-center gap-2 bg-[#0E2240] text-white text-sm px-4 py-2 rounded-sm hover:bg-[#1a3358] transition-colors duration-200">
              <Plus size={15} /> Raise Requisition
            </button>
          )}
        </div>
      </div>

      <div className="flex border-b border-gray-200 mb-5" data-testid="req-tabs">
        {TABS.map(([k, label]) => (
          <button key={k} data-testid={`req-tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-2.5 text-sm border-b-2 -mb-px transition-colors duration-200 ${
              tab === k ? "border-[#BF9B30] text-[#0E2240] font-medium" : "border-transparent text-[#6B7280] hover:text-[#0E2240]"}`}>
            {label}
            {k === "tray" && tray.length > 0 && (
              <span data-testid="tray-count" className="ml-2 bg-[#D97706] text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-sm tabular-nums">{tray.length}</span>
            )}
          </button>
        ))}
      </div>

      {tab === "tray" ? (
        tray.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-sm p-8 text-sm text-[#6B7280]">No auto-drafted requisitions pending submission.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="auto-draft-tray">
            {tray.map((r) => (
              <div key={r.req_id} data-testid={`tray-card-${r.req_id}`} className="bg-white border border-[#D97706]/40 rounded-sm p-5">
                <div className="flex items-start justify-between">
                  <div className="text-xs font-semibold text-[#0E2240] tabular-nums">{r.req_no}</div>
                  <AgeBadge days={r.ageing_days} />
                </div>
                <div className="font-serif text-lg text-[#0E2240] mt-1.5">{r.desig_name}</div>
                <div className="text-xs text-[#6B7280]">{r.branch_name} · {r.dept_name}</div>
                <div className="mt-3 text-xs text-[#6B7280] leading-snug">
                  Replacing <span className="font-medium text-[#0E2240]">{r.replacing_emp_name}</span> ({r.replacing_emp_code})
                  {r.expected_doj && <> · target joining <span className="tabular-nums">{r.expected_doj}</span></>}
                </div>
                <div className="mt-4 flex gap-2">
                  <button data-testid={`tray-view-${r.req_id}`} onClick={() => navigate(`/requisitions/${r.req_id}`)}
                    className="flex-1 border border-gray-300 text-sm py-2 rounded-sm hover:bg-gray-50 transition-colors duration-200">Review</button>
                  {can("requisitions", "write") && (
                    <button data-testid={`tray-submit-${r.req_id}`} disabled={busy === r.req_id} onClick={() => submit(r)}
                      className="flex-1 flex items-center justify-center gap-2 bg-[#0E2240] text-white text-sm py-2 rounded-sm hover:bg-[#1a3358] transition-colors duration-200 disabled:opacity-50">
                      <Send size={14} /> Submit
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        <>
          {tab === "all" && (
            <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="req-filters">
              <select data-testid="req-status-filter" className={selCls} value={f.status} onChange={(e) => setF((p) => ({ ...p, status: e.target.value }))}>
                <option value="">All statuses</option>
                {MRF_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select data-testid="req-type-filter" className={selCls} value={f.requisition_type} onChange={(e) => setF((p) => ({ ...p, requisition_type: e.target.value }))}>
                <option value="">All types</option>
                {MRF_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <select data-testid="req-priority-filter" className={selCls} value={f.priority} onChange={(e) => setF((p) => ({ ...p, priority: e.target.value }))}>
                <option value="">All priorities</option>
                {MRF_PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <select data-testid="req-sla-filter" className={selCls} value={f.sla_status} onChange={(e) => setF((p) => ({ ...p, sla_status: e.target.value }))}>
                <option value="">All SLA health</option>
                {["On Track", "At Risk", "Breached", "On Hold"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select data-testid="req-branch-filter" className={selCls} value={f.branch_id} onChange={(e) => setF((p) => ({ ...p, branch_id: e.target.value }))}>
                <option value="">All branches</option>
                {maps.branches.map((b) => <option key={b.branch_id} value={b.branch_id}>{b.branch_name}</option>)}
              </select>
              <input data-testid="req-search" className={`${inputCls} !w-44`} placeholder="Search req no…"
                value={f.search} onChange={(e) => setF((p) => ({ ...p, search: e.target.value }))} />
            </div>
          )}

          <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="requisitions-table">
                <thead>
                  <tr className="border-b border-gray-200 bg-[#F5F6F8]">
                    {["Req No", "Type", "Position", "Branch", "Pos", "Priority", "Stuck with", "Days", "SLA", "Status"].map((h, i) => (
                      <th key={i} className="px-3 py-3 text-left text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={10} className="px-4 py-10 text-center text-[#6B7280]">Loading…</td></tr>
                  ) : loadError ? (
                    <tr><td colSpan={10} className="px-4 py-10 text-center">
                      <span data-testid="req-load-error" className="text-[#BB162B] text-sm">Could not load requisitions.</span>
                      <button onClick={load} className="ml-2 text-sm text-[#1E5AA8] hover:underline">Retry</button>
                    </td></tr>
                  ) : list.length === 0 ? (
                    <tr><td colSpan={10} className="px-4 py-10 text-center text-[#6B7280]">No requisitions</td></tr>
                  ) : list.slice(0, limit).map((r) => (
                    <tr key={r.req_id} data-testid={`req-row-${r.req_id}`} onClick={() => navigate(`/requisitions/${r.req_id}`)}
                      className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors duration-200 cursor-pointer">
                      <td className="px-3 py-2.5 whitespace-nowrap text-xs font-medium text-[#0E2240] tabular-nums">
                        {r.req_no}
                        {r.auto_drafted && <span className="ml-1.5 text-[9px] uppercase text-[#D97706] border border-[#D97706]/30 px-1 rounded-sm">Auto</span>}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap"><TypeBadge type={r.requisition_type} forced={r.forced_beyond} /></td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        <div className="text-[#0E2240]">{r.desig_name}</div>
                        <div className="text-[10px] text-[#6B7280]">{r.dept_name}</div>
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-xs">{r.branch_name}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-xs tabular-nums">{r.positions_filled ?? 0}/{r.positions_count ?? 1}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-xs">
                        <span className={r.priority === "Critical" ? "text-[#BB162B] font-semibold" : ""}>{r.priority}</span>
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-xs">
                        <div className="text-[#0E2240]">{r.current_owner_name || "—"}</div>
                        <div className="text-[10px] text-[#6B7280]">{r.current_stage}</div>
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap"><DaysInStage days={r.days_in_current_stage} sla={r.sla_status} /></td>
                      <td className="px-3 py-2.5 whitespace-nowrap"><SlaChip sla={r.sla_status} /></td>
                      <td className="px-3 py-2.5"><StatusBadge status={r.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-2.5 border-t border-gray-200 text-xs text-[#6B7280] tabular-nums flex items-center justify-between">
              <span>Showing {Math.min(limit, list.length)} of {list.length} requisitions</span>
              {list.length > limit && (
                <button data-testid="req-show-more" onClick={() => setLimit((l) => l + 50)} className="text-[#1E5AA8] hover:underline">
                  Show 50 more
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
